import "./App.css";
import React, { Suspense, lazy, useEffect, useState } from "react";
import { ConfigProvider } from "antd";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { store } from "./store";

import { Provider } from "react-redux";
import setAuthToken from "./utils/setAuthToken";
import AdminRoute from "./routes/AdminRoute";
import Sidebar from "./layout/Sidebar";
import Navbar from "./layout/Navbar";
import { saveConfig, setAdmin } from "./actions/AuthAction";
import { setConfig } from "./reducers/configReducer";

import { isPermitted } from "./utils/PermissionManager";
import { setProfile } from "./reducers/authReducer";
import Bottombar from "./layout/BottomBar";
import { siteInfo } from "./utils/SiteInfo";

const OfficeComplains = lazy(() => import("./pages/OfficeComplains"));
const IdeasForAll = lazy(() => import("./pages/IdeasForAll"));
const RslActivityLog = lazy(() => import("./pages/RslActivityLog"));
const RslBalanceReport = lazy(() => import("./pages/RslBalanceReport"));
const BalanceTransactions = lazy(() => import("./pages/BalanceTransaction"));

const RSLAdvancedPaymentReport = lazy(
  () => import("./pages/RSLAdvancedPaymentReport"),
);
const RSLAdvancedDueReport = lazy(
  () => import("./components/RslAdvancedDueReport"),
);

const RslOrderReport = lazy(() => import("./pages/RSLOrderReport"));
const BecommercePayment = lazy(() => import("./pages/BcommercePayment"));

const RmbTransactionDetails = lazy(
  () => import("./pages/RmbTranscationDetails"),
);

const RmbTransactionReport = lazy(() => import("./pages/RmbTransactionReport"));
const CourierPaymentReport = lazy(() => import("./pages/CourierPaymentReport"));
const SkyExpressReport = lazy(() => import("./pages/SkyExpressReport"));

const InactiveCustomerReport = lazy(
  () => import("./pages/InactiveCustomerReport"),
);
const PointCustomer = lazy(() => import("./pages/PointCustomers"));
const Customers = lazy(() => import("./pages/Customers"));
const Orders = lazy(() => import("./pages/Orders"));
const Payments = lazy(() => import("./pages/Payments"));

const CustomerReport = lazy(() => import("./pages/CustomerReport"));

const WarehouseCourierReport = lazy(
  () => import("./pages/WarehouseCourierReport"),
);
const DeliveryDueReport = lazy(() => import("./pages/DeliveryDueReport"));

const ShipmentInvoiceReport = lazy(
  () => import("./pages/ShipmentInvoiceReport"),
);

const EmployeePurchasingReport = lazy(
  () => import("./pages/EmployeePurchasingReport"),
);

const BankReport = lazy(() => import("./pages/BankReport"));
const InvestmentReport = lazy(() => import("./pages/InvestmentReport"));
const EmployeePurchaseReport = lazy(
  () => import("./pages/EmployeePurchaseReport"),
);

const RefundReport = lazy(() => import("./pages/RefundReport"));

const DeliveryPaymentReport = lazy(
  () => import("./pages/DeliveryPaymentReport"),
);

const SteadFastReport = lazy(() => import("./pages/SteadFastReport"));

const PurchaseReport = lazy(() => import("./pages/PurchaseReport"));
const CallReport = lazy(() => import("./pages/CallReport"));
const CallTaskReport = lazy(() => import("./pages/CallTaskReport"));
const AdvanceStatusTimeline = lazy(() => import("./pages/AdvanceStatusTimeline"));
const GateWayReport = lazy(() => import("./pages/GatewayReport"));
const PaymentReport = lazy(() => import("./pages/PaymentReport"));

const SmsReport = lazy(() => import("./pages/SmsReport"));
const DatewiseBadgeReport = lazy(() => import("./pages/DatewiseBadgeReport"));
const ManagerWiseBadgeReport = lazy(
  () => import("./pages/ManagerWiseBadgeReport"),
);
const BadgeCustomerReport = lazy(() => import("./pages/BadgeCustomerReport"));
const CashReport = lazy(() => import("./pages/CashReport"));
const ProfitReport = lazy(() => import("./pages/ProfitReport"));
const MonthlyReport = lazy(() => import("./pages/MonthlyReport"));
const ActivityReport = lazy(() => import("./pages/ActivityReport"));
const DueReport = lazy(() => import("./pages/DueReport"));
const UnseenReport = lazy(() => import("./pages/UnseenReport"));
const UnrepliedReport = lazy(() => import("./pages/UnrepliedReport"));
const SlowOrders = lazy(() => import("./pages/SlowOrders"));
const Login = lazy(() => import("./pages/Login"));
const Profile = lazy(() => import("./pages/Profile"));
const ComingSoon = lazy(() => import("./pages/ComingSoon"));

if (localStorage.config) {
  let config = JSON.parse(localStorage.config);
  let profile = config.profile;
  store.dispatch(setProfile(profile));
  store.dispatch(setConfig(config));
}

if (localStorage.skyadminrep) {
  setAuthToken(localStorage.skyadminrep);
  store.dispatch(setAdmin(true));
  store.dispatch(saveConfig());
}

const Dashboard = lazy(() => import("./pages/Dashboard"));

function App() {
  const client = window.screen.width > 768 ? "web" : "app";
  const [show, setshow] = useState(client === "web" ? false : true);
  const [trigger, settrigger] = useState(false);

  let phone = store.getState().auth?.profile?.phone;

  const roles = store.getState().auth?.profile?.roles
    ? store.getState().auth?.profile?.roles
    : [];

  const activated = store.getState().auth?.activated;

  return (
    <Provider store={store}>
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: "#D1412A",
          },
        }}
      >
        <BrowserRouter>
          <Suspense fallback={<div className="pageProgress"></div>}>
            <div className="App">
              <Sidebar state={{ show, setshow }} />
              <div>
                <Navbar state={{ settrigger, setshow, show }} />
                {roles?.length > 0 && <Bottombar />}
                <Routes>
                  <Route
                    exact
                    path="/"
                    element={
                      <AdminRoute
                        state={{ show, trigger }}
                        component={<Dashboard />}
                      />
                    }
                  />

                  {activated && (
                    <>
                      <Route
                        exact
                        path="/customers"
                        element={
                          <AdminRoute
                            state={{ show, trigger }}
                            component={<Customers />}
                          />
                        }
                      />

                      <Route
                        exact
                        path="/orders"
                        element={
                          <AdminRoute
                            state={{ show, trigger }}
                            component={<Orders />}
                          />
                        }
                      />
                      {isPermitted(roles, ["admin"]) && (
                        <Route
                          exact
                          path="/shipment-report/:id?"
                          element={
                            <AdminRoute
                              state={{ show, trigger }}
                              component={<ShipmentInvoiceReport />}
                            />
                          }
                        />
                      )}
                      {isPermitted(roles, ["admin"]) && (
                        <Route
                          exact
                          path="/delivery-due-report"
                          element={
                            <AdminRoute
                              state={{ show, trigger }}
                              component={<DeliveryDueReport />}
                            />
                          }
                        />
                      )}

                      {isPermitted(roles, ["admin"]) && (
                        <Route
                          exact
                          path="/warehouse-courier-report"
                          element={
                            <AdminRoute
                              state={{ show, trigger }}
                              component={<WarehouseCourierReport />}
                            />
                          }
                        />
                      )}

                      {isPermitted(roles, ["super_admin", "admin"]) && (
                        <Route
                          exact
                          path="/monthly-report"
                          element={
                            <AdminRoute
                              state={{ show, trigger }}
                              component={<MonthlyReport />}
                            />
                          }
                        />
                      )}
                      {isPermitted(roles, ["super_admin", "admin"]) && (
                        <Route
                          exact
                          path="/report/advanced/slow-orders"
                          element={
                            <AdminRoute
                              state={{ show, trigger }}
                              component={<SlowOrders />}
                            />
                          }
                        />
                      )}
                      {isPermitted(roles, ["super_admin", "admin"]) && (
                        <Route
                          exact
                          path="/customer-report"
                          element={
                            <AdminRoute
                              state={{ show, trigger }}
                              component={<CustomerReport />}
                            />
                          }
                        />
                      )}

                      {isPermitted(roles, ["admin", "super_admin"]) && (
                        <Route
                          exact
                          path="/balance-transactions"
                          element={
                            <AdminRoute
                              state={{ show, trigger }}
                              component={<BalanceTransactions />}
                            />
                          }
                        />
                      )}

                      {isPermitted(roles, ["super_admin", "admin"]) && (
                        <Route
                          exact
                          path="/inactive-customer-report"
                          element={
                            <AdminRoute
                              state={{ show, trigger }}
                              component={<InactiveCustomerReport />}
                            />
                          }
                        />
                      )}
                      {isPermitted(roles, ["super_admin", "admin"]) && (
                        <Route
                          exact
                          path="/point-customer"
                          element={
                            <AdminRoute
                              state={{ show, trigger }}
                              component={<PointCustomer />}
                            />
                          }
                        />
                      )}
                      {isPermitted(roles, ["super_admin", "admin"]) && (
                        <Route
                          exact
                          path="/bank-report"
                          element={
                            <AdminRoute
                              state={{ show, trigger }}
                              component={<BankReport />}
                            />
                          }
                        />
                      )}
                      {isPermitted(roles, ["super_admin", "admin"]) && (
                        <Route
                          exact
                          path="/investment-report"
                          element={
                            <AdminRoute
                              state={{ show, trigger }}
                              component={<InvestmentReport />}
                            />
                          }
                        />
                      )}
                      {isPermitted(roles, ["super_admin", "admin"]) && (
                        <Route
                          exact
                          path="/employee-purchase-report"
                          element={
                            <AdminRoute
                              state={{ show, trigger }}
                              component={<EmployeePurchaseReport />}
                            />
                          }
                        />
                      )}
                      {isPermitted(roles, ["super_admin", "admin"]) && (
                        <Route
                          exact
                          path="/employee-purchasing-report"
                          element={
                            <AdminRoute
                              state={{ show, trigger }}
                              component={<EmployeePurchasingReport />}
                            />
                          }
                        />
                      )}
                      {isPermitted(roles, ["super_admin", "admin"]) && (
                        <Route
                          exact
                          path="/rmb-transaction-report"
                          element={
                            <AdminRoute
                              state={{ show, trigger }}
                              component={<RmbTransactionReport />}
                            />
                          }
                        />
                      )}
                      {isPermitted(roles, ["super_admin", "admin"]) && (
                        <Route
                          exact
                          path="/rmb-transaction-details/:id"
                          element={
                            <AdminRoute
                              state={{ show, trigger }}
                              component={<RmbTransactionDetails />}
                            />
                          }
                        />
                      )}
                      {isPermitted(roles, ["super_admin", "admin"]) && (
                        <Route
                          exact
                          path="/steadfast-report"
                          element={
                            <AdminRoute
                              state={{ show, trigger }}
                              component={<SteadFastReport />}
                            />
                          }
                        />
                      )}
                      {isPermitted(roles, ["super_admin", "admin"]) && (
                        <Route
                          exact
                          path="/skyexpress-report"
                          element={
                            <AdminRoute
                              state={{ show, trigger }}
                              component={<SkyExpressReport />}
                            />
                          }
                        />
                      )}
                      {isPermitted(roles, [
                        "super_admin",
                        "admin",
                        "accountant",
                        "delivery",
                      ]) && (
                          <Route
                            exact
                            path="/delivery-payment-report"
                            element={
                              <AdminRoute
                                state={{ show, trigger }}
                                component={<DeliveryPaymentReport />}
                              />
                            }
                          />
                        )}

                      {isPermitted(roles, ["super_admin", "admin"]) && (
                        <Route
                          exact
                          path="/refund-report"
                          element={
                            <AdminRoute
                              state={{ show, trigger }}
                              component={<RefundReport />}
                            />
                          }
                        />
                      )}

                      {isPermitted(roles, ["super_admin"]) && (
                        <Route
                          exact
                          path="/profit-report"
                          element={
                            <AdminRoute
                              state={{ show, trigger }}
                              component={<ProfitReport />}
                            />
                          }
                        />
                      )}
                      {isPermitted(roles, ["admin"]) && (
                        <Route
                          exact
                          path="/gateway-report"
                          element={
                            <AdminRoute
                              state={{ show, trigger }}
                              component={<GateWayReport />}
                            />
                          }
                        />
                      )}
                      {isPermitted(roles, ["admin"]) && (
                        <Route
                          exact
                          path="/purchase-report"
                          element={
                            <AdminRoute
                              state={{ show, trigger }}
                              component={<PurchaseReport />}
                            />
                          }
                        />
                      )}
                      {isPermitted(roles, ["admin", "accountant"]) && (
                        <Route
                          exact
                          path="/payments"
                          element={
                            <AdminRoute
                              state={{ show, trigger }}
                              component={<Payments />}
                            />
                          }
                        />
                      )}
                      {isPermitted(roles, ["admin"]) && (
                        <Route
                          exact
                          path="/payment-report"
                          element={
                            <AdminRoute
                              state={{ show, trigger }}
                              component={<PaymentReport />}
                            />
                          }
                        />
                      )}
                      {isPermitted(roles, ["admin"]) && (
                        <Route
                          exact
                          path="/courier-payment-report"
                          element={
                            <AdminRoute
                              state={{ show, trigger }}
                              component={<CourierPaymentReport />}
                            />
                          }
                        />
                      )}
                      {isPermitted(roles, ["admin"]) && (
                        <Route
                          exact
                          path="/sms-report"
                          element={
                            <AdminRoute
                              state={{ show, trigger }}
                              component={<SmsReport />}
                            />
                          }
                        />
                      )}
                      {isPermitted(roles, ["admin"]) && (
                        <Route
                          exact
                          path="/cash-report"
                          element={
                            <AdminRoute
                              state={{ show, trigger }}
                              component={<CashReport />}
                            />
                          }
                        />
                      )}
                      {isPermitted(roles, ["master_admin"]) && (
                        <Route
                          exact
                          path="/ideas"
                          element={
                            <AdminRoute
                              state={{ show, trigger }}
                              component={<IdeasForAll />}
                            />
                          }
                        />
                      )}
                      {isPermitted(roles, ["super_admin", "admin", 'master_admin']) && (
                        <>
                          <Route
                            exact
                            path="/report/badge/monthly-report"
                            element={
                              <AdminRoute
                                state={{ show, trigger }}
                                component={<DatewiseBadgeReport />}
                              />
                            }
                          />
                          <Route
                            exact
                            path="/report/badge/manager-report"
                            element={
                              <AdminRoute
                                state={{ show, trigger }}
                                component={<ManagerWiseBadgeReport />}
                              />
                            }
                          />
                          <Route
                            exact
                            path="/report/badge/customers"
                            element={
                              <AdminRoute
                                state={{ show, trigger }}
                                component={<BadgeCustomerReport />}
                              />
                            }
                          />
                        </>
                      )}
                      {isPermitted(roles, ["admin"]) && (
                        <Route
                          exact
                          path="/call-report"
                          element={
                            <AdminRoute
                              state={{ show, trigger }}
                              component={<CallReport />}
                            />
                          }
                        />
                      )}
                      {isPermitted(roles, ["admin"]) && (
                        <Route
                          exact
                          path="/call-task-report"
                          element={
                            <AdminRoute
                              state={{ show, trigger }}
                              component={<CallTaskReport />}
                            />
                          }
                        />
                      )}
                      {isPermitted(roles, ["admin"]) && (
                        <Route
                          exact
                          path="/advanced-status-timeline"
                          element={
                            <AdminRoute
                              state={{ show, trigger }}
                              component={<AdvanceStatusTimeline />}
                            />
                          }
                        />
                      )}
                      {isPermitted(roles, ["admin"]) && (
                        <Route
                          exact
                          path="/activity-report"
                          element={
                            <AdminRoute
                              state={{ show, trigger }}
                              component={<ActivityReport />}
                            />
                          }
                        />
                      )}
                      {isPermitted(roles, ["admin"]) && (
                        <Route
                          exact
                          path="/due-report"
                          element={
                            <AdminRoute
                              state={{ show, trigger }}
                              component={<DueReport />}
                            />
                          }
                        />
                      )}
                      {isPermitted(roles, ["admin"]) && (
                        <Route
                          exact
                          path="/unseen-report"
                          element={
                            <AdminRoute
                              state={{ show, trigger }}
                              component={<UnseenReport />}
                            />
                          }
                        />
                      )}
                      {isPermitted(roles, ["admin"]) && (
                        <Route
                          exact
                          path="/unreplied-report"
                          element={
                            <AdminRoute
                              state={{ show, trigger }}
                              component={<UnrepliedReport />}
                            />
                          }
                        />
                      )}
                      {isPermitted(roles, ["master_admin"]) && (
                        <Route
                          exact
                          path="/bcommerce-payment"
                          element={
                            <AdminRoute
                              state={{ show, trigger }}
                              component={<BecommercePayment />}
                            />
                          }
                        />
                      )}

                      {isPermitted(roles, ["super_admin"]) &&
                        siteInfo.masterAccess?.includes(
                          phone,
                        ) && (
                          <Route
                            exact
                            path="/master-order-report"
                            element={
                              <AdminRoute
                                state={{ show, trigger }}
                                component={<RslOrderReport />}
                              />
                            }
                          />
                        )}
                      {isPermitted(roles, ["master_admin"]) &&
                        siteInfo.masterAccess?.includes(
                          phone,
                        ) && (
                          <Route
                            exact
                            path="/office-complains"
                            element={
                              <AdminRoute
                                state={{ show, trigger }}
                                component={<OfficeComplains />}
                              />
                            }
                          />
                        )}
                      {isPermitted(roles, ["super_admin"]) &&
                        siteInfo.masterAccess?.includes(
                          phone,
                        ) && (
                          <Route
                            exact
                            path="/master-balance-report"
                            element={
                              <AdminRoute
                                state={{ show, trigger }}
                                component={<RslBalanceReport />}
                              />
                            }
                          />
                        )}
                      {isPermitted(roles, ["super_admin"]) &&
                        siteInfo.masterAccess?.includes(
                          phone,
                        ) && (
                          <Route
                            exact
                            path="/master-activity-log"
                            element={
                              <AdminRoute
                                state={{ show, trigger }}
                                component={<RslActivityLog />}
                              />
                            }
                          />
                        )}
                      {isPermitted(roles, ["super_admin"]) &&
                        siteInfo.masterAccess?.includes(
                          phone,
                        ) && (
                          <Route
                            exact
                            path="/master-payment-report"
                            element={
                              <AdminRoute
                                state={{ show, trigger }}
                                component={<RSLAdvancedPaymentReport />}
                              />
                            }
                          />
                        )}
                      {isPermitted(roles, ["super_admin"]) &&
                        siteInfo.masterAccess?.includes(
                          phone,
                        ) && (
                          <Route
                            exact
                            path="/master-due-report"
                            element={
                              <AdminRoute
                                state={{ show, trigger }}
                                component={<RSLAdvancedDueReport />}
                              />
                            }
                          />
                        )}

                      <Route
                        exact
                        path="/profile"
                        element={
                          <AdminRoute
                            state={{ show, trigger }}
                            component={<Profile />}
                          />
                        }
                      />
                    </>
                  )}



                  <Route exact path="/login" element={<Login />} />
                  <Route
                    path="*"
                    element={
                      <AdminRoute
                        state={{ show, trigger }}
                        component={
                          <div
                            style={{
                              height: "calc(100vh - 78px)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "#fff",
                            }}
                          >
                            <ComingSoon />
                          </div>
                        }
                      />
                    }
                  />
                </Routes>
              </div>
            </div>
          </Suspense>
        </BrowserRouter>
      </ConfigProvider>
    </Provider>
  );
}

export default App;
